import { ColDef } from 'ag-grid-community';
import { CurrencyDollar } from 'react-bootstrap-icons';
import { useMemo } from 'react';
import { BaseReport } from './BaseReport';
import { ReportConfig } from './types';
import { Api } from '../Data/Api';
import { cents, showMoney } from '../Data/Money';
import { toLocalDateTime } from '../Data/Date';
import { PayoutSummaryReportApiResponse } from '../Data/ApiTypes';
import { ServerTransferSavedReportResponse } from '../shared/api/axios-client';

interface PayoutSummaryFilter {
    page: number;
    pageSize: number;
    operatorId?: number;
    from?: string;
    to?: string;
}

type PayoutSummaryGridRow = {
    operatorId: number;
    facilityId: number;
    facilityName: string;
    payoutKey: string;
    payoutDate: PayoutSummaryReportApiResponse['payoutDate'];
    totalAmount: ReturnType<typeof cents>;
};

const columns: ColDef<PayoutSummaryGridRow>[] = [
    { field: 'facilityName', headerName: 'Facility', hide: false, filter: false },
    { field: 'payoutKey', headerName: 'Payout Key', hide: false, filter: false },
    {
        field: 'payoutDate',
        headerName: 'Payout Date',
        hide: false,
        filter: false,
        valueFormatter: (p) => toLocalDateTime(p.value),
    },
    {
        field: 'totalAmount',
        headerName: 'Total Amount',
        hide: false,
        filter: false,
        valueFormatter: (p) => showMoney(p.value),
    },
];

export function PayoutSummaryReport({
    operatorId,
    savedReports,
    setSavedReports,
    heightOffset = 50,
}: {
    operatorId: number;
    savedReports: ServerTransferSavedReportResponse[];
    setSavedReports: React.Dispatch<React.SetStateAction<ServerTransferSavedReportResponse[]>>;
    heightOffset?: number;
}) {
    const config = useMemo<ReportConfig<PayoutSummaryGridRow, PayoutSummaryFilter>>(
        () => ({
            id: 'payoutsummary',
            title: 'Payout Summary Report',
            icon: <CurrencyDollar style={{ fontSize: '1.5rem', marginTop: '-10px' }} />,
            description: `<p>This report summarizes payouts by facility and payout key for the selected date range.</p>`,
            defaultColumns: columns,
            filterFields: [],
            defaultFilterForm: {
                operatorId,
                from: '',
                to: '',
                quickDateMode: 'All',
            },
            getData: async (filter) => {
                if (!operatorId) {
                    throw new Error('Operator context is required');
                }
                const response = await Api.payoutSummaryReports({
                    ...filter,
                    operatorId,
                });
                if (!response.isOk) throw new Error(response.error);
                return {
                    total: response.value.total,
                    items: response.value.items.map((r) => ({
                        ...r,
                        totalAmount: cents(r.totalAmount),
                    })),
                };
            },
        }),
        [operatorId]
    );

    return (
        <BaseReport
            config={config}
            savedReports={savedReports}
            setSavedReports={setSavedReports}
            heightOffset={heightOffset}
            storageKey="report_columns_payout_summary"
        />
    );
}
